import { useEffect, useState } from 'react';
import { formatInteger } from '@brimo/shared';
import { Banner, Button, Dialog, Skeleton, TextField } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { archiveProperty, useArchivePreview } from '../api';
import type { PropertyRecord } from '../types';
import styles from './dialogs.module.css';

export interface ArchiveDialogProps {
  open: boolean;
  record: PropertyRecord;
  onClose: () => void;
  onArchived: (record: PropertyRecord) => void;
}

/** Objekte 07: the consequences of `GET /properties/:id/archive-preview`, the warning, the mandatory reason. */
export function ArchiveDialog({ open, record, onClose, onArchived }: ArchiveDialogProps) {
  const preview = useArchivePreview(record.id, open);
  const [reason, setReason] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (open) {
      setReason('');
      setFieldError(undefined);
      setError(null);
    }
  }, [open]);

  async function submit() {
    if (!reason.trim()) {
      setFieldError('Bitte geben Sie einen Grund an.');
      return;
    }
    setFieldError(undefined);
    setError(null);
    setPending(true);
    try {
      onArchived(await archiveProperty(record.id, reason.trim()));
    } catch (err) {
      if (err instanceof ApiError && err.fields['reason']) setFieldError(err.fields['reason']);
      else setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  const data = preview.data;
  const deals = data?.activeDeals ?? 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow="Objekt archivieren"
      title={`${record.name} archivieren?`}
      subtitle="Das Objekt verschwindet aus der Übersicht und der Suche nach freien Einheiten."
      width={560}
      footerNote="Wiederherstellen jederzeit durch Admin"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button loading={pending} onClick={() => void submit()}>
            Archivieren
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {preview.isError ? <Banner tone="danger">{errorMessage(preview.error)}</Banner> : null}
        <div className={styles.consequences}>
          <ConsequenceRow
            label="Freie Einheiten"
            value={
              data ? `${formatInteger(data.availableUnits)} · werden auf „nicht verfügbar“ gesetzt` : null
            }
          />
          <ConsequenceRow
            label="Laufende Deals"
            value={data ? `${formatInteger(data.activeDeals)} · behalten Objekt und Einheit` : null}
          />
          <ConsequenceRow
            label="Reservierungen"
            value={
              data
                ? `${formatInteger(data.liveReservations)} · ${data.liveReservations === 1 ? 'bleibt' : 'bleiben'} bestehen`
                : null
            }
          />
          <ConsequenceRow label="Dokumente" value="bleiben im Datenraum lesbar" />
        </div>
        {deals > 0 ? (
          <div className={styles.warningBox} role="status">
            {deals === 1
              ? '1 Deal läuft noch. Er bleibt unverändert und zeigt das Objekt mit dem Hinweis „archiviert“.'
              : `${formatInteger(deals)} Deals laufen noch. Sie bleiben unverändert und zeigen das Objekt mit dem Hinweis „archiviert“.`}
          </div>
        ) : null}
        <TextField
          label="Grund"
          required
          value={reason}
          error={fieldError}
          placeholder="z. B. Abverkauf abgeschlossen, Restwohnungen gehen an Bauträger zurück"
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
    </Dialog>
  );
}

function ConsequenceRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className={styles.consequence}>
      <span className={styles.consequenceLabel}>{label}</span>
      <span className={styles.consequenceValue}>{value ?? <Skeleton width={180} height={14} />}</span>
    </div>
  );
}
